import { Bus, Locate, Minus, Plus } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { TrackedVehicle } from '@/types/transport';
import { ROUTE_TONE } from '../constants';

/**
 * One line per route, as a path across the plate.
 *
 * Hand-drawn geometry rather than a tile layer: a map provider is a network
 * request to a third party and a key in the bundle, and this section is being
 * laid out before any of that is decided. The shape is honest about being a
 * schematic — localities in their rough relative positions, one line per route,
 * a marker where each vehicle has reached — so the panels can be judged, and
 * the `<svg>` swapped for a real map without either of them moving.
 *
 * Its own component because two screens draw it: the dashboard's tracking card
 * and the Tracking tab's full-width board (CLAUDE.md §3). What differs between
 * them is what sits *around* the plate, not the plate.
 *
 * The five line colours come from `ROUTE_TONE`, the one place in this section
 * allowed to name a hue: four routes crossing each other have to stay
 * distinguishable, which a single accent cannot do.
 */
const ROUTE_PATHS: Array<{ tone: keyof typeof ROUTE_TONE; d: string; at: [number, number] }> = [
  { tone: 'emerald', d: 'M 96 232 C 130 200, 150 150, 168 96', at: [96, 232] },
  { tone: 'sky', d: 'M 168 96 C 230 70, 300 78, 352 118 S 430 168, 470 150', at: [168, 96] },
  { tone: 'violet', d: 'M 470 150 C 520 128, 566 140, 592 176', at: [592, 176] },
  { tone: 'amber', d: 'M 352 230 C 330 268, 300 292, 262 300', at: [262, 300] },
  { tone: 'rose', d: 'M 352 230 C 372 266, 380 292, 372 322', at: [372, 322] },
];

/** Where the schematic prints a locality name. */
const PLACES: Array<{ name: string; x: number; y: number }> = [
  { name: 'Gachibowli', x: 168, y: 78 },
  { name: 'Madhapur', x: 470, y: 60 },
  { name: 'Jubilee Hills', x: 352, y: 118 },
  { name: 'Hitech City', x: 594, y: 152 },
  { name: 'Banjara Hills', x: 492, y: 238 },
  { name: 'Kondapur', x: 100, y: 262 },
  { name: 'Manikonda', x: 300, y: 318 },
];

export interface RouteMapProps {
  vehicles: TrackedVehicle[];
  className?: string;
}

export function RouteMap({ vehicles, className }: RouteMapProps) {
  return (
    <div
      className={cn(
        'relative min-h-[280px] flex-1 overflow-hidden rounded-2xl border border-line bg-surface-2',
        className,
      )}
    >
      <svg
        viewBox="0 0 660 360"
        className="h-full w-full"
        role="img"
        aria-label={`Schematic of ${vehicles.length} vehicles on their routes`}
        preserveAspectRatio="xMidYMid slice"
      >
        {/* The ground grid, in the line token so it reads in both themes. */}
        <defs>
          <pattern id="transport-grid" width="30" height="30" patternUnits="userSpaceOnUse">
            <path d="M 30 0 L 0 0 0 30" fill="none" className="stroke-line" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width="660" height="360" fill="url(#transport-grid)" />

        {ROUTE_PATHS.map((route) => (
          <path
            key={route.tone}
            d={route.d}
            fill="none"
            strokeWidth="3.5"
            strokeLinecap="round"
            className={ROUTE_TONE[route.tone].stroke}
          />
        ))}

        {PLACES.map((place) => (
          <g key={place.name}>
            <circle cx={place.x} cy={place.y + 14} r="3" className="fill-ink-3" />
            <text
              x={place.x}
              y={place.y}
              textAnchor="middle"
              className="fill-ink-2 text-[11px] font-semibold"
            >
              {place.name}
            </text>
          </g>
        ))}

        {ROUTE_PATHS.slice(0, vehicles.length).map((route, index) => (
          <g key={`marker-${route.tone}`} transform={`translate(${route.at[0]}, ${route.at[1]})`}>
            <circle r="11" className="fill-surface stroke-line" strokeWidth="2" />
            <circle r="5" className={cn(ROUTE_TONE[vehicles[index].tone].dot, 'fill-current')} />
          </g>
        ))}
      </svg>

      {/* Map controls. Inert until a real map is behind them — they carry no
          handler rather than a handler that does nothing. */}
      <div className="absolute bottom-3 right-3 flex flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
        {[Plus, Minus, Locate].map((Icon, index) => (
          <span
            key={index}
            className="grid h-8 w-8 place-items-center border-b border-line text-ink-3 last:border-b-0"
            aria-hidden
          >
            <Icon className="h-4 w-4" />
          </span>
        ))}
      </div>

      <p className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-pill bg-surface px-2.5 py-1 text-xs font-bold text-ink-2 shadow-sm">
        <Bus className="h-3.5 w-3.5 text-accent" aria-hidden />
        Schematic view
      </p>
    </div>
  );
}
